import { parseAlgorithm } from '../core/moves.js';
import { SOLVER_STATUS } from './solver-client.js';

function now() {
  return globalThis.performance?.now?.() ?? Date.now();
}

function noopStatus(_status) {}

async function defaultLoader() {
  const [{ KPattern }, { cube2x2x2 }, { experimentalSolve2x2x2 }] = await Promise.all([
    import('cubing/kpuzzle'),
    import('cubing/puzzles'),
    import('cubing/search'),
  ]);
  return { KPattern, cube2x2x2, solvePattern: experimentalSolve2x2x2 };
}

export class SolverClient2x2 {
  constructor({ loader = defaultLoader, timeoutMs = 30000 } = {}) {
    this.loader = loader;
    this.timeoutMs = timeoutMs;
    this.runtimePromise = null;
  }

  async initialize(onStatus = noopStatus) {
    if (!this.runtimePromise) {
      onStatus(SOLVER_STATUS.INITIALIZING);
      this.runtimePromise = this.loader().then(async (loaded) => ({
        ...loaded,
        kpuzzle: await loaded.cube2x2x2.kpuzzle(),
      })).catch((cause) => {
        this.runtimePromise = null;
        throw cause;
      });
    }
    return this.runtimePromise;
  }

  async solve(patternData, { onStatus = noopStatus } = {}) {
    const startedAt = now();
    try {
      const runtime = await this.initialize(onStatus);
      const pattern = new runtime.KPattern(runtime.kpuzzle, patternData);
      onStatus(SOLVER_STATUS.SOLVING);
      let timer;
      const algorithm = await Promise.race([
        runtime.solvePattern(pattern),
        new Promise((_, reject) => {
          timer = setTimeout(() => reject(Object.assign(new Error('2×2 求解超过 30 秒，已停止等待'), { code: 'TIMEOUT' })), this.timeoutMs);
        }),
      ]).finally(() => clearTimeout(timer));
      const moves = parseAlgorithm(algorithm.toString());
      const formula = moves.join(' ');
      if (!pattern.applyAlg(formula).isIdentical(runtime.kpuzzle.defaultPattern())) {
        throw Object.assign(new Error('求解器返回的公式未能还原当前 2×2 状态，已拒绝播放'), { code: 'RESULT_MISMATCH' });
      }
      onStatus(SOLVER_STATUS.COMPLETE);
      return {
        formula,
        moves,
        moveCount: moves.length,
        durationMs: Math.round(now() - startedAt),
        diagnostics: [],
      };
    } catch (cause) {
      const error = Object.assign(new Error(`2×2求解未完成。${cause?.message ?? String(cause)}`, { cause }), {
        code: cause?.code ?? 'SOLVER_RUNTIME_ERROR',
      });
      error.name = 'SolverError';
      throw error;
    }
  }
}
